import { useSyncExternalStore } from "react";

/**
 * Theme system for apps/web.
 *
 * The design system ships a complete `.dark` token set in `styles/globals.css`;
 * applying the `dark` class to `<html>` (document.documentElement) flips the
 * whole app. This module owns that class and the user's preference.
 *
 * Resolution order for the effective theme:
 *   1. An explicit, persisted user choice in `localStorage("theme")`
 *      (`"light"` | `"dark"`).
 *   2. Otherwise the OS preference via
 *      `matchMedia("(prefers-color-scheme: dark)")`.
 *
 * While NO explicit choice is stored, the effective theme tracks the OS in real
 * time (we listen for matchMedia `change`). Once the user makes an explicit
 * choice, that choice wins and the OS is no longer followed (until the choice is
 * cleared from storage).
 *
 * A tiny render-blocking script in `index.html` applies the same logic before
 * first paint (anti-FOUC); this module keeps React authoritative afterwards.
 */

export type Theme = "light" | "dark";

const STORAGE_KEY = "theme";
const COLOR_SCHEME_QUERY = "(prefers-color-scheme: dark)";

/**
 * True when running in a browser with a usable document.
 * @returns Whether `window` and `document` are available.
 */
function isBrowser(): boolean {
  return typeof window !== "undefined" && typeof document !== "undefined";
}

/**
 * Read the persisted explicit choice, if any.
 * Storage access is wrapped in try/catch — Safari private mode and disabled
 * storage throw on access.
 * @returns The stored theme, or null when there is no explicit choice.
 */
function readStoredTheme(): Theme | null {
  try {
    const value = window.localStorage.getItem(STORAGE_KEY);
    return value === "light" || value === "dark" ? value : null;
  } catch {
    return null;
  }
}

/**
 * Persist (or clear) the explicit user choice.
 * @param theme - The theme to persist, or null to clear the explicit choice.
 */
function writeStoredTheme(theme: Theme | null): void {
  try {
    if (theme === null) {
      window.localStorage.removeItem(STORAGE_KEY);
    } else {
      window.localStorage.setItem(STORAGE_KEY, theme);
    }
  } catch {
    // Ignore — storage is best-effort; the in-memory state still drives the UI.
  }
}

/**
 * Read the current OS color-scheme preference.
 * @returns `"dark"` when the OS prefers dark, otherwise `"light"`.
 */
function getSystemTheme(): Theme {
  if (!isBrowser() || typeof window.matchMedia !== "function") {
    return "light";
  }
  return window.matchMedia(COLOR_SCHEME_QUERY).matches ? "dark" : "light";
}

/**
 * Resolve the effective theme from storage (explicit choice) then OS.
 * @returns The effective theme.
 */
function resolveTheme(): Theme {
  return readStoredTheme() ?? getSystemTheme();
}

/**
 * Apply (or remove) the `dark` class on the document root.
 * @param theme - The theme to apply.
 */
function applyTheme(theme: Theme): void {
  if (!isBrowser()) return;
  document.documentElement.classList.toggle("dark", theme === "dark");
}

// --- Module store -----------------------------------------------------------
// A minimal external store so every useTheme() consumer re-renders together
// and shares one matchMedia subscription.

let currentTheme: Theme = isBrowser() ? resolveTheme() : "light";
const listeners = new Set<() => void>();
let mediaListenerAttached = false;

/** Notify all subscribed React components that the theme changed. */
function emit(): void {
  for (const listener of listeners) listener();
}

/**
 * Set the effective theme in the store and apply it, optionally persisting.
 * @param theme - The new effective theme.
 * @param persist - When true, also persist as an explicit user choice.
 */
function setThemeInternal(theme: Theme, persist: boolean): void {
  if (persist) writeStoredTheme(theme);
  if (theme === currentTheme) {
    // Class may still be stale relative to the store — keep it in sync.
    applyTheme(theme);
    return;
  }
  currentTheme = theme;
  applyTheme(theme);
  emit();
}

/**
 * Handle an OS color-scheme change — only honored when no explicit choice.
 * @param event - The matchMedia change event.
 */
function handleSystemChange(event: MediaQueryListEvent): void {
  if (readStoredTheme() !== null) return; // explicit choice wins
  setThemeInternal(event.matches ? "dark" : "light", false);
}

/** Lazily attach the matchMedia change subscription (idempotent). */
function ensureMediaSubscription(): void {
  if (mediaListenerAttached || !isBrowser() || typeof window.matchMedia !== "function") {
    return;
  }
  window.matchMedia(COLOR_SCHEME_QUERY).addEventListener("change", handleSystemChange);
  mediaListenerAttached = true;
}

/**
 * Subscribe a React component (via useSyncExternalStore) to theme changes.
 * The first subscriber attaches the OS-preference listener.
 * @param listener - Called whenever the effective theme changes.
 * @returns An unsubscribe function.
 */
function subscribe(listener: () => void): () => void {
  ensureMediaSubscription();
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Snapshot getter for useSyncExternalStore.
 * @returns The current effective theme.
 */
function getSnapshot(): Theme {
  return currentTheme;
}

/**
 * Server snapshot — there is no DOM, default to light.
 * @returns Always `"light"`.
 */
function getServerSnapshot(): Theme {
  return "light";
}

/**
 * Initialize the theme at app startup.
 *
 * Re-resolves the effective theme, applies the `dark` class (in case the
 * render-blocking head script was blocked or storage changed), and attaches the
 * OS-preference listener so the app follows the system while no explicit choice
 * is stored. Safe to call once at the React root; idempotent.
 */
export function initTheme(): void {
  if (!isBrowser()) return;
  currentTheme = resolveTheme();
  applyTheme(currentTheme);
  ensureMediaSubscription();
  emit();
}

/**
 * Set an explicit theme choice. Persists to localStorage, applies the `dark`
 * class, and stops following the OS preference.
 * @param theme - The theme to switch to.
 */
export function setTheme(theme: Theme): void {
  setThemeInternal(theme, true);
}

/** Toggle between light and dark, persisting the result as an explicit choice. */
export function toggleTheme(): void {
  setTheme(currentTheme === "dark" ? "light" : "dark");
}

/**
 * Clear the explicit user choice and revert to following the OS preference.
 * Primarily a testing/escape hatch; not wired to UI in this unit.
 */
export function clearThemePreference(): void {
  writeStoredTheme(null);
  setThemeInternal(getSystemTheme(), false);
}

/**
 * React hook exposing the effective theme plus a setter and toggle.
 * @returns `{ theme, setTheme, toggleTheme }`.
 */
export function useTheme(): {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
} {
  const theme = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
  return { theme, setTheme, toggleTheme };
}
